import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  HomeIcon,
  DocumentTextIcon,
  ArrowUpTrayIcon,
  Cog6ToothIcon,
  Bars3BottomLeftIcon,
} from '@heroicons/react/24/outline';
import {
  HomeIcon as HomeIconSolid,
  DocumentTextIcon as DocumentTextIconSolid,
  ArrowUpTrayIcon as ArrowUpTrayIconSolid,
  Cog6ToothIcon as Cog6ToothIconSolid,
} from '@heroicons/react/24/solid';

const navItems = [
  {
    name: 'Home',
    href: '/dashboard',
    icon: HomeIcon,
    activeIcon: HomeIconSolid,
  },
  {
    name: 'Documents',
    href: '/documents',
    icon: DocumentTextIcon,
    activeIcon: DocumentTextIconSolid,
  },
  {
    name: 'Upload',
    href: '/upload',
    icon: ArrowUpTrayIcon,
    activeIcon: ArrowUpTrayIconSolid,
    isFab: true,
  },
  {
    name: 'Settings',
    href: '/settings',
    icon: Cog6ToothIcon,
    activeIcon: Cog6ToothIconSolid,
  },
  {
    name: 'More',
    icon: Bars3BottomLeftIcon,
    activeIcon: Bars3BottomLeftIcon,
    isMore: true,
  },
];

export default function MobileBottomNav({ onMoreClick }) {
  const location = useLocation();

  const isActive = (href) => {
    if (!href) return false;
    if (href === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bg-theme-secondary border-t border-theme-primary safe-area-bottom">
      <div className="flex items-center justify-around h-16 px-2">
        {navItems.map((item) => {
          const active = isActive(item.href);
          const Icon = active ? item.activeIcon : item.icon;

          // FAB (Upload button)
          if (item.isFab) {
            return (
              <Link
                key={item.name}
                to={item.href}
                className="relative -mt-6"
              >
                <motion.div
                  whileTap={{ scale: 0.9 }}
                  className="w-14 h-14 rounded-full bg-gradient-to-br from-brand-orange to-brand-amber flex items-center justify-center shadow-lg shadow-brand-orange/30"
                >
                  <Icon className="w-7 h-7 text-white" />
                </motion.div>
              </Link>
            );
          }

          // More button
          if (item.isMore) {
            return (
              <button
                key={item.name}
                onClick={onMoreClick}
                className="flex flex-col items-center justify-center w-16 py-1"
              >
                <Icon className="w-6 h-6 text-theme-muted" />
                <span className="text-xs mt-1 text-theme-muted">{item.name}</span>
              </button>
            );
          }

          // Regular nav items
          return (
            <Link
              key={item.name}
              to={item.href}
              className="flex flex-col items-center justify-center w-16 py-1"
            >
              <motion.div
                initial={false}
                animate={{
                  scale: active ? 1.1 : 1,
                }}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              >
                <Icon
                  className={`w-6 h-6 ${
                    active ? 'text-brand-orange' : 'text-theme-muted'
                  }`}
                />
              </motion.div>
              <span
                className={`text-xs mt-1 ${
                  active ? 'text-brand-orange font-medium' : 'text-theme-muted'
                }`}
              >
                {item.name}
              </span>
              {active && (
                <motion.div
                  layoutId="bottomNavIndicator"
                  className="absolute -bottom-0 w-12 h-0.5 bg-brand-orange rounded-full"
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                />
              )}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
